import { unwrap, type Schemas } from '@swarm/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export type Plant = Schemas['PlantDto'];
export type SavePlantRequest = Schemas['SavePlantRequest'];
export type ForageCalendar = Schemas['ForageCalendarDto'];
export type CalendarEntry = Schemas['CalendarEntryDto'];
export type ForageEntryRequest = Schemas['ForageEntryRequest'];
export type Weather = Schemas['WeatherDto'];
export type Condition = Schemas['ConditionDto'];
export type Blooming = Schemas['BloomingDto'];
export type ForageSuggestions = Schemas['ForageSuggestionsDto'];
export type ForageSuggestion = Schemas['ForageSuggestionDto'];
export type LandscapeEvidence = Schemas['LandscapeEvidenceDto'];

const keys = {
  all: ['forage'] as const,
  plants: ['forage', 'plants'] as const,
  calendar: (location: string) => ['forage', 'calendar', location] as const,
  weather: (location: string) => ['forage', 'weather', location] as const,
  suggestions: (location: string) => ['forage', 'suggestions', location] as const,
  blooming: ['forage', 'blooming'] as const,
};

const ifMatch = (version?: number): Record<string, string> =>
  version === undefined ? {} : { 'If-Match': `"${version}"` };

export function usePlants(enabled = true) {
  return useQuery({
    queryKey: keys.plants,
    queryFn: async () => unwrap(await api.GET('/forage/plants')),
    enabled,
    staleTime: 60_000,
  });
}

export function useSavePlant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
      version,
    }: {
      id?: string | undefined;
      body: SavePlantRequest;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/forage/plants/{plant_id}', {
              params: { path: { plant_id: id } },
              body,
              headers: ifMatch(version),
            }),
          )
        : unwrap(await api.POST('/forage/plants', { body })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useForageCalendar(location: string, enabled = true) {
  return useQuery({
    queryKey: keys.calendar(location),
    queryFn: async () =>
      unwrap(
        await api.GET('/locations/{location_id}/forage', {
          params: { path: { location_id: location } },
        }),
      ),
    enabled,
  });
}

export function useSaveForage(location: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ entries, version }: { entries: ForageEntryRequest[]; version: number }) =>
      unwrap(
        await api.PUT('/locations/{location_id}/forage', {
          params: { path: { location_id: location } },
          body: { entries },
          headers: ifMatch(version),
        }),
      ),
    onSuccess: (calendar) => {
      client.setQueryData(keys.calendar(location), calendar);
      return Promise.all([
        client.invalidateQueries({ queryKey: keys.blooming }),
        client.invalidateQueries({ queryKey: keys.suggestions(location) }),
      ]);
    },
  });
}

/** Weather from the backend's cache; refreshed now and then while open. */
export function useWeather(location: string, enabled = true) {
  return useQuery({
    queryKey: keys.weather(location),
    queryFn: async () =>
      unwrap(
        await api.GET('/locations/{location_id}/weather', {
          params: { path: { location_id: location } },
        }),
      ),
    enabled,
    refetchInterval: (query) => (query.state.data?.state === 'unavailable' ? 60_000 : 15 * 60_000),
  });
}

export function useBlooming(enabled = true) {
  return useQuery({
    queryKey: keys.blooming,
    queryFn: async () => unwrap(await api.GET('/forage/blooming')),
    enabled,
  });
}

/**
 * Plants the surroundings offer (TRA-007). The backend fetches them in the
 * background; while pending, ask again shortly.
 */
export function useForageSuggestions(location: string, enabled = true) {
  return useQuery({
    queryKey: keys.suggestions(location),
    queryFn: async () =>
      unwrap(
        await api.GET('/locations/{location_id}/forage/suggestions', {
          params: { path: { location_id: location } },
        }),
      ),
    enabled,
    staleTime: 60_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.state === 'pending') return 3_000;
      // Reading after the wait starts the next try.
      if (data?.state === 'unavailable' && data.retry_at)
        return Math.max(Date.parse(data.retry_at) - Date.now() + 2_000, 5_000);
      return false;
    },
  });
}

/** Fetches the surroundings again after a failure (TRA-007); answers `pending`. */
export function useRetrySuggestions(location: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/locations/{location_id}/forage/suggestions/retry', {
          params: { path: { location_id: location } },
        }),
      ),
    onSuccess: (data) => client.setQueryData(keys.suggestions(location), data),
  });
}
